import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { GET, POST, DELETE, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

/**
 * KUNLIK ISH — sex kuniga qancha ish qilganini qayd etadi.
 *
 * IKKI RAQAM YONMA-YON (2026-08-18, korxona egasining talabi):
 *
 *   1. QO'LDA — usta o'zi yozadi: «bugun bo'lim shuncha metr ish qildi».
 *      Bitta raqam, mahsulot nomlari va rulon ro'yxati YO'Q (ular olib tashlandi:
 *      egaga kerak bo'lgani «аксессуар бугун 1000 метр иш қилди» degan raqam).
 *
 *   2. CHEKLAR BO'YICHA — kengashda «Tayyor» bosilgan cheklardan hisoblanadi.
 *      Hech kim yozmaydi, o'zi chiqadi.
 *
 * Rahbar ikkalasini solishtiradi: farq katta bo'lsa — yo usta noto'g'ri yozgan,
 * yo chek kengashda o'tkazilmagan. Shuning uchun ikkisi HAM kerak, biri
 * ikkinchisini almashtirmaydi.
 *
 * BO'LIMLAR: uchala sex ham (Aksessuar 3, Profnastil 4, Tunukafon 5) bir xil
 * to'ldiradi. Kraskachilarda (6/7) raqam kg da.
 *
 * ⚠️ Bu hisobot FAQAT RAQAM uchun. Ombor qoldig'i, tannarx, cheklar —
 *    hech biriga ta'siri yo'q (user talabi: «hech nmaga boglama»).
 */

const PAINT_TYPES = [6, 7]           // kraskachilar
const UNITS = { 3: 'metr', 4: 'metr', 5: 'kv.m' }
const unitOf = id => UNITS[Number(id)] || 'dona'

const trim = (v, d = 2) => {
	const n = Number(v || 0)
	return Number.isFinite(n) ? String(Number(n.toFixed(d))) : '0'
}

export default function DailyWork() {
	const reduxAccount = useSelector(state => state.account)
	const today = formatDate(new Date(), 'yyyy-MM-dd')

	const [myTypes, setMyTypes] = useState([])
	const [logs, setLogs] = useState([])

	// Cheklar bo'yicha hisoblangan ish (tanlangan sana + bo'lim uchun)
	const [calc, setCalc] = useState(null)
	const [calcLoading, setCalcLoading] = useState(false)

	const defaultForm = () => ({
		work_date: today,
		product_type_id: '',
		workers_count: 1,
		paint_kg: '',
		total_metr: '',
		note: '',
		items: [],
	})
	const [form, setForm] = useState(defaultForm())

	const ptId    = Number(form.product_type_id) || 0
	const isPaint = PAINT_TYPES.includes(ptId)
	const unit    = unitOf(ptId)

	/* MAHSULOT RO'YXATI — FAQAT TUNUKAFONDA (tur 5).
	   Sabab (korxona egasi, 2026-08-18): tunikofonda bir kunda 2-3 xil mahsulot
	   chiqadi, shuning uchun jami kv.metrdan tashqari qaysi mahsulotdan qancha
	   qilingani ham kerak. Aksessuar va profnastilda bunday bo'linish so'ralmadi —
	   u yerda faqat bitta jami raqam. */
	const hasItems = ptId === 5

	const addItem = () => setForm(p => ({ ...p, items: [...(p.items || []), { product_name: '', quantity: '' }] }))
	const removeItem = i => setForm(p => ({ ...p, items: (p.items || []).filter((_, x) => x !== i) }))
	const updateItem = (i, field, value) => setForm(p => {
		const items = [...(p.items || [])]
		items[i] = { ...items[i], [field]: value }
		return { ...p, items }
	})

	const itemsSum = (form.items || []).reduce(
		(a, it) => a + (Number(String(it.quantity).replace(',', '.')) || 0), 0)

	async function loadProductTypes() {
		const res = await GET('/helper/products-types')
		if (!httpOk(res)) return
		const mine = reduxAccount.product_types || []
		const filtered = res.data.filter(pt => mine.includes(pt.id))
		setMyTypes(filtered)
		if (filtered.length >= 1) {
			setForm(prev => ({ ...prev, product_type_id: filtered[0].id }))
		}
	}

	async function loadMyLogs() {
		const res = await GET('/daily-work/my')
		if (httpOk(res)) setLogs(res.data)
	}

	const loadComputed = useCallback(async (date, pt) => {
		if (!pt || !date) return
		setCalcLoading(true)
		const res = await GET('/daily-work/computed', { work_date: date, product_type_id: pt }, { loader: false })
		setCalc(httpOk(res) ? res.data : null)
		setCalcLoading(false)
	}, [])

	useEffect(() => {
		loadProductTypes()
		loadMyLogs()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	/* Sana yoki bo'lim o'zgarganda — cheklardagi raqam ham yangilanadi.
	   Endi bu HAMMA bo'lim uchun ishlaydi (ilgari faqat profnastilda edi). */
	useEffect(() => {
		loadComputed(form.work_date, ptId)
	}, [ptId, form.work_date, loadComputed])

	/* Shu kun uchun allaqachon yozilgan qo'lda hisobot — usta ikkinchi marta
	   kirsa bo'sh forma emas, o'zi yozgan raqamni ko'rsin (saqlasa yangilanadi). */
	const savedToday = logs.find(l =>
		String(l.work_date).slice(0, 10) === form.work_date
		&& Number(l.product_type_id) === ptId
		&& l.source !== 'auto')

	/* Sana yoki bo'lim o'zgarganda o'sha kunga yozilgani formaga tushadi —
	   usta o'zi yozgan raqamni ko'rib turadi, saqlasa yangilanadi.
	   Yozuv bo'lmasa maydonlar tozalanadi (oldingi kunniki qolib ketmasin). */
	useEffect(() => {
		setForm(p => ({
			...p,
			total_metr: savedToday && Number(savedToday.total_metr) > 0 ? trim(savedToday.total_metr, 2) : '',
			paint_kg: savedToday && Number(savedToday.paint_kg) > 0 ? trim(savedToday.paint_kg, 2) : '',
			workers_count: savedToday ? (savedToday.workers_count || 1) : 1,
			note: savedToday ? (savedToday.note || '') : '',
			/* ⚠️ Mahsulotlar HAM qaytariladi: saqlaganda eski qatorlar o'chirilib
			   qaytadan yoziladi, ya'ni formada bo'lmasa yo'qolib ketardi. */
			items: savedToday && savedToday.items && savedToday.items.length
				? savedToday.items.map(it => ({ product_name: it.product_name, quantity: trim(it.quantity, 2) }))
				: (ptId === 5 ? [{ product_name: '', quantity: '' }] : []),
		}))
	}, [savedToday?.id, form.work_date, ptId]) // eslint-disable-line react-hooks/exhaustive-deps

	/* TUNUKAFONDA jami raqam ALOHIDA yozilmaydi — u mahsulotlar yig'indisi
	   (korxona egasi, 2026-08-18: «бугун қилган ишни олиб ташла, пастда
	   маҳсулотлар бўйича тургани қолса етади»). Qolgan bo'limlarda
	   avvalgidek bitta jami raqam yoziladi. */
	const manual = hasItems
		? itemsSum
		: (Number(String(form.total_metr).replace(',', '.')) || 0)
	const auto = Number(calc?.total || 0)
	const diff = manual > 0 && auto > 0 ? manual - auto : null
	const diffPct = diff !== null && auto > 0 ? Math.round((diff / auto) * 100) : null

	async function submit(e) {
		e.preventDefault()
		if (!form.product_type_id) { toast.error("Bo'limni tanlang"); return }
		if (!isPaint && manual <= 0) {
			toast.error(hasItems ? 'Mahsulot va miqdorini kiriting' : 'Qilingan ishni kiriting')
			return
		}

		// Ishchilar soni bo'sh qolgan bo'lsa (maydonni tozalab, qayta yozmagan) — 1
		const workers = Number(form.workers_count) >= 1 ? Math.floor(Number(form.workers_count)) : 1

		// Mahsulot ro'yxati faqat tunikofonda yuboriladi (boshqa bo'limda maydon yo'q)
		const items = hasItems
			? (form.items || []).filter(it => it.product_name && String(it.product_name).trim())
			: []

		// Tunukafonda jami — mahsulotlar yig'indisi (alohida maydon yo'q)
		const res = await POST('/daily-work',
			{ ...form, workers_count: workers, total_metr: manual, items, unit }, { loader: true })
		if (httpOk(res)) {
			toast.success(savedToday ? 'Yangilandi' : 'Saqlandi')
			loadMyLogs()
		}
	}

	async function deleteLog(id) {
		const res = await DELETE('/daily-work/' + id)
		if (httpOk(res)) loadMyLogs()
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-3">
				<h4 className="vertical-center mb-0">Kunlik ish hisoboti</h4>
				<span className="text-muted vertical-center">{reduxAccount.name}</span>
			</div>

			{/* Bo'lim tanlovi — bir nechta bo'limga biriktirilgan usta uchun */}
			{myTypes.length > 1 && (
				<div className="dw-tabs mb-3">
					{myTypes.map(pt => (
						<button key={pt.id}
							className={'dw-tab' + (Number(form.product_type_id) === pt.id ? ' is-active' : '')}
							onClick={() => setForm(p => ({ ...p, product_type_id: pt.id }))}>
							{pt.name}
						</button>
					))}
				</div>
			)}

			<div className="row">
				{/* ─────────── QO'LDA YOZILADIGAN QISM ─────────── */}
				<div className="col-md-5">
					<div className="card">
						<div className="card-body">
							<h6 className="card-title fw-bold mb-3">
								{savedToday ? 'Bugungi yozuv (tahrirlash)' : 'Yangi yozuv'}
							</h6>
							<form onSubmit={submit}>
								<div className="form-group">
									<label>Sana <span className="required-mark">*</span></label>
									<input type="date" className="form-control" value={form.work_date}
										onChange={e => setForm({ ...form, work_date: e.target.value })} />
								</div>

								{myTypes.length === 1 && (
									<div className="form-group">
										<label>Bo'lim</label>
										<div className="form-control bg-light">{myTypes[0].name}</div>
									</div>
								)}

								<div className="form-group">
									<label>Bugun ishlagan ishchilar soni <span className="required-mark">*</span></label>
									{/* ⚠️ Maydon BO'SH qolishiga ruxsat beriladi. Ilgari har bosishda
									    `Math.max(1, ...)` ishlab, «1» ni o'chirishning iloji yo'q edi —
									    usta 5 yozmoqchi bo'lsa ham maydon darrov 1 ga qaytardi.
									    Haqiqiy qiymat saqlashda tekshiriladi (kamida 1). */}
									<input type="number" className="form-control" min="1" step="1"
										value={form.workers_count}
										onChange={e => setForm({ ...form, workers_count: e.target.value })}
										onBlur={() => setForm(p => ({
											...p,
											workers_count: Number(p.workers_count) >= 1 ? Math.floor(Number(p.workers_count)) : 1,
										}))} />
								</div>

								{isPaint && (
									<div className="form-group">
										<label>Kraska (kg)</label>
										<input type="number" className="form-control" step="0.001" min="0"
											value={form.paint_kg}
											onChange={e => setForm({ ...form, paint_kg: e.target.value })} />
									</div>
								)}

								{/* ASOSIY RAQAM — «bugun bo'lim shuncha ish qildi».
								    ⚠️ Tunukafonda bu maydon YO'Q: u yerda jami mahsulotlar
								    ro'yxatidan yig'iladi va o'ng tomondagi jamlanmada chiqadi. */}
								{!hasItems && (
									<div className="form-group">
										<label>
											Bugun qilingan ish ({unit}) <span className="required-mark">*</span>
										</label>
										<input type="number" className="form-control form-control-lg" step="0.01" min="0"
											placeholder={auto > 0 ? trim(auto, 2) : ''}
											value={form.total_metr}
											onChange={e => setForm({ ...form, total_metr: e.target.value })} />

										{/* Cheklardagi raqam — yonida, solishtirish uchun */}
										<div className="dw-cmp mt-2">
											<span className="dw-cmp-l">
												<i className="uil-receipt me-1" />Cheklar bo'yicha:
											</span>
											{calcLoading
												? <b className="text-muted">...</b>
												: <b>{trim(auto, 2)} {unit}</b>}
											{auto > 0 && (
												<button type="button" className="dw-cmp-btn"
													title="Shu raqamni qo'yish"
													onClick={() => setForm({ ...form, total_metr: trim(auto, 2) })}>
													olish
												</button>
											)}
											{diff !== null && Math.abs(diff) >= 0.01 && (
												<span className={'dw-cmp-d ' + (diff > 0 ? 'is-plus' : 'is-minus')}>
													{diff > 0 ? '+' : ''}{trim(diff, 2)}
													{diffPct !== null && ` (${diffPct > 0 ? '+' : ''}${diffPct}%)`}
												</span>
											)}
										</div>
									</div>
								)}

								{/* MAHSULOTLAR — faqat tunikofonda: bir kunda 2-3 xil chiqadi */}
								{hasItems && (
									<div className="form-group">
										<label className="d-block mb-2">
											Bugun qilingan ish — mahsulotlar bo'yicha <span className="required-mark">*</span>
											<span className="text-muted ms-2" style={{ fontSize: 11, fontWeight: 400 }}>
												nomi va miqdori ({unit}); jami o'zi hisoblanadi
											</span>
										</label>
										{(form.items || []).map((item, idx) => (
											<div key={idx} className="d-flex gap-2 mb-2">
												<input type="text" className="form-control" placeholder="Mahsulot nomi"
													value={item.product_name}
													onChange={e => updateItem(idx, 'product_name', e.target.value)} />
												<input type="number" className="form-control" placeholder={unit}
													min="0" step="0.001" style={{ maxWidth: 110 }}
													value={item.quantity}
													onChange={e => updateItem(idx, 'quantity', e.target.value)} />
												<button type="button" className="btn btn-outline-danger px-2"
													onClick={() => removeItem(idx)}>
													<i className="uil-times" />
												</button>
											</div>
										))}
										<button type="button" className="btn btn-outline-secondary btn-sm" onClick={addItem}>
											<i className="uil-plus me-1" />Mahsulot qo'shish
										</button>
										{/* Jami — shu yerda ham ko'rinib tursin (asosiysi o'ng tomonda) */}
										{itemsSum > 0 && (
											<div className="dw-cmp mt-2">
												<span className="dw-cmp-l">Jami:</span>
												<b>{trim(itemsSum, 2)} {unit}</b>
												<span className="dw-cmp-l ms-2">
													<i className="uil-receipt me-1" />Cheklar bo'yicha:
												</span>
												<b>{trim(auto, 2)} {unit}</b>
												{diff !== null && Math.abs(diff) >= 0.01 && (
													<span className={'dw-cmp-d ' + (diff > 0 ? 'is-plus' : 'is-minus')}>
														{diff > 0 ? '+' : ''}{trim(diff, 2)}
														{diffPct !== null && ` (${diffPct > 0 ? '+' : ''}${diffPct}%)`}
													</span>
												)}
											</div>
										)}
									</div>
								)}

								<div className="form-group">
									<label>Izoh</label>
									<textarea className="form-control" rows="2" value={form.note}
										onChange={e => setForm({ ...form, note: e.target.value })} />
								</div>

								<button type="submit" className="btn btn-primary w-100 mt-2">
									<i className="uil-check me-1" />{savedToday ? 'Yangilash' : 'Saqlash'}
								</button>

								{savedToday && (
									<div className="text-muted text-center mt-2" style={{ fontSize: 12 }}>
										Bu kunga yozilgan: <b>{trim(savedToday.total_metr, 2)} {unit}</b> — saqlasangiz yangilanadi
									</div>
								)}
							</form>
						</div>
					</div>
				</div>

				<div className="col-md-7">
					{/* ─────────── CHEKLAR BO'YICHA (avtomat) ─────────── */}
					<div className="card">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<h6 className="card-title fw-bold mb-0">
									Cheklar bo'yicha bajarilgan
									<span className="text-muted ms-2" style={{ fontSize: 11, fontWeight: 400 }}>
										kengashda «Tayyor» bosilgan cheklar
									</span>
								</h6>
								<span className="text-muted" style={{ fontSize: 12 }}>
									{formatDate(form.work_date, 'dd.MM.yyyy')}
								</span>
							</div>

							{calcLoading && <div className="text-center text-muted py-3">Hisoblanmoqda...</div>}

							{!calcLoading && (
								<>
									<div className="dw-sum">
										<div className="dw-sum-box">
											<span>Qo'lda yozilgan</span>
											<b>{trim(manual || (savedToday ? savedToday.total_metr : 0), 2)} <em>{unit}</em></b>
										</div>
										<div className="dw-sum-box">
											<span>Cheklar bo'yicha</span>
											<b>{trim(auto, 2)} <em>{unit}</em></b>
										</div>
										<div className="dw-sum-box">
											<span>Cheklar</span>
											<b>{calc?.cheque_count ?? 0}</b>
										</div>
									</div>

									{/* TUNUKAFON — jami qaysi mahsulotlardan yig'ilgani.
									    Chapda faqat kiritish, jamlanma shu yerda turadi. */}
									{hasItems && itemsSum > 0 && (
										<div className="mb-3">
											{(form.items || []).filter(it => it.product_name).map((it, i) => (
												<span key={i} className="badge bg-light text-dark border me-1 mb-1">
													{it.product_name}: <b>{trim(it.quantity, 2)} {unit}</b>
												</span>
											))}
											{diff !== null && Math.abs(diff) >= 0.01 && (
												<span className={'dw-cmp-d ms-1 ' + (diff > 0 ? 'is-plus' : 'is-minus')}
													style={{ fontSize: 12 }}>
													cheklardan {diff > 0 ? '+' : ''}{trim(diff, 2)} {unit}
													{diffPct !== null && ` (${diffPct > 0 ? '+' : ''}${diffPct}%)`}
												</span>
											)}
										</div>
									)}

									{!calc || calc.cheques.length === 0 ? (
										<div className="text-center text-muted py-3">
											Bu kunda tayyor bo'lgan chek yo'q
										</div>
									) : (
										<div className="table-responsive">
											<table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
												<thead>
													<tr>
														<th style={{ width: 80 }}>Chek</th>
														<th>Mijoz</th>
														<th style={{ width: 90 }}>Tayyor</th>
														<th className="text-end" style={{ width: 90 }}>Miqdor</th>
													</tr>
												</thead>
												<tbody>
													{calc.cheques.map((c, i) => (
														<tr key={i}>
															<td className="fw-bold">#{c.cheque_no || c.id}</td>
															<td>{c.client_name || '—'}</td>
															<td className="text-nowrap">{formatDate(c.ready_at, 'HH:mm')}</td>
															<td className="text-end fw-bold">{trim(c.amount, 2)}</td>
														</tr>
													))}
												</tbody>
												<tfoot>
													<tr className="table-light">
														<td colSpan="3" className="fw-bold">JAMI</td>
														<td className="text-end fw-bold">{trim(calc.total, 2)} {calc.unit}</td>
													</tr>
												</tfoot>
											</table>
										</div>
									)}
								</>
							)}
						</div>
					</div>

					{/* ─────────── YOZUVLAR TARIXI ─────────── */}
					<div className="card mt-3">
						<div className="card-body">
							<h6 className="card-title fw-bold mb-3">Mening yozuvlarim</h6>
							{logs.length === 0 ? (
								<div className="text-center text-muted py-4">Hozircha yozuvlar yo'q</div>
							) : (
								<div className="table-responsive">
									<table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
										<thead>
											<tr>
												<th>Sana</th>
												<th>Bo'lim</th>
												<th className="text-center">Ishchi</th>
												<th className="text-end">Qilingan ish</th>
												<th>Mahsulot / Izoh</th>
												<th style={{ width: 40 }}></th>
											</tr>
										</thead>
										<tbody>
											{logs.map((log, idx) => (
												<tr key={idx}>
													<td className="text-nowrap">{formatDate(log.work_date, 'dd.MM.yyyy')}</td>
													<td>{log.product_type_name}</td>
													<td className="text-center fw-bold">{log.workers_count ?? '—'}</td>
													<td className="text-end fw-bold text-nowrap">
														{Number(log.total_metr) > 0
															? trim(log.total_metr, 2) + ' ' + unitOf(log.product_type_id)
															: Number(log.paint_kg) > 0
																? trim(log.paint_kg, 2) + ' kg'
																: '—'}
													</td>
													<td style={{ fontSize: 12 }}>
														{/* Tunikofonning mahsulotlari — yozilgan bo'lsa */}
														{log.items && log.items.length > 0 && (
															<div className="mb-1">
																{log.items.map((item, i) => (
																	<span key={i} className="badge bg-light text-dark border me-1 mb-1">
																		{item.product_name}: <b>{trim(item.quantity, 2)}</b>
																	</span>
																))}
															</div>
														)}
														<span className="text-muted">{log.note || (log.items && log.items.length ? '' : '—')}</span>
													</td>
													<td className="text-center">
														<button className="btn btn-sm btn-outline-danger p-1"
															style={{ lineHeight: 1 }}
															onClick={() => deleteLog(log.id)}>
															<i className="uil-trash-alt" />
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<style>{DW_CSS}</style>
		</>
	)
}

const DW_CSS = `
.dw-tabs{display:flex;gap:6px;flex-wrap:wrap}
.dw-tab{padding:7px 16px;border:1px solid #e3e8f0;border-radius:8px;background:#fff;
	color:#64748b;font-size:13.5px;font-weight:600;cursor:pointer}
.dw-tab:hover{background:#f8fafc}
.dw-tab.is-active{background:#2563eb;border-color:#2563eb;color:#fff}

.dw-sum{display:flex;gap:8px;margin-bottom:14px}
.dw-sum-box{flex:1 1 0;padding:9px 12px;border:1px solid #e3e8f0;border-radius:9px;
	background:#f8fafc;min-width:0}
.dw-sum-box span{display:block;font-size:11px;color:#94a3b8;text-transform:uppercase;
	letter-spacing:.3px;margin-bottom:2px}
.dw-sum-box b{font-size:19px;font-weight:800;color:#1e293b;font-variant-numeric:tabular-nums}
.dw-sum-box b em{font-size:12px;font-weight:600;color:#64748b;font-style:normal;margin-left:3px}

/* Qo'lda yozilgan raqam ostidagi solishtirish qatori */
.dw-cmp{display:flex;align-items:center;flex-wrap:wrap;gap:7px;font-size:12.5px;
	font-variant-numeric:tabular-nums}
.dw-cmp-l{color:#94a3b8}
.dw-cmp b{color:#1e293b;font-weight:700}
.dw-cmp-btn{padding:1px 8px;border:1px solid #cfe0ff;border-radius:6px;background:#eff5ff;
	color:#1d4ed8;font-size:11.5px;font-weight:600;cursor:pointer}
.dw-cmp-btn:hover{background:#dfeaff}
.dw-cmp-d{padding:1px 7px;border-radius:6px;font-weight:700}
.dw-cmp-d.is-plus{background:#fff4e5;color:#b45309}
.dw-cmp-d.is-minus{background:#fee9e9;color:#b42318}
`
